import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { OutlinedInput } from "@mui/material";
import { Field,Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";

import KinesinLogo from "assets/images/auth/kinesin-logo.svg";
import { AppVersion } from "components/AppVersion/AppVersion";
import { InlineVideoCall } from "components/InlineVideoCall/InlineVideoCall";
import { getMicrophonePermissionState } from "components/InlineVideoCall/videoCallDiagnostics";
import { DateOfBirthInput } from "features/comunications/components/DateOfBirthInput/DateOfBirthInput";
import { NAME_AND_YEAR_OF_BIRTH_LINK_VERSION } from "features/comunications/constants/videoJoinVerification";
import { useParticipantJoinVideoStandalone } from "features/comunications/hooks/useParticipantJoinVideoStandalone";
import { formatDateOfBirth, isValidDateOfBirth } from "features/comunications/utils/dateOfBirth";

import { AuthLayout } from "../components/AuthLayout/AuthLayout";
import styles from "./ParticipantVideoJoinPage.module.css";

// Stops iOS rewriting what the patient types, so "O'Neill" isn't sent as "O’Neill".
// spellcheck="false" is what turns off Smart Punctuation (curly quotes, "--" → "—") in
// WebKit; autocorrect="off" stops names being "corrected" to dictionary words.
const NAME_INPUT_PROPS = {
  autoCorrect: "off",
  autoCapitalize: "words",
  spellCheck: "false",
};

const YEAR_INPUT_PROPS = { inputMode: "numeric" };

const nameValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters"),
  surName: Yup.string()
    .required("Surname is required")
    .min(2, "Surname must be at least 2 characters")
    .max(50, "Surname must be less than 50 characters"),
  yearOfBirth: Yup.string()
    .required("Year of birth is required eg 1999")
    .matches(/^\d{4}$/, "Year must be 4 digits (e.g., 1990)")
    .test("valid-year", "Please enter a valid year", (value) => {
      if (!value) return false;
      const year = parseInt(value, 10);
      const currentYear = new Date().getFullYear();
      return year >= 1900 && year <= currentYear;
    }),
});

const dateOfBirthValidationSchema = Yup.object().shape({
  birthDay: Yup.string()
    .required("Day is required")
    .matches(/^\d{1,2}$/, "Day must be a number, e.g. 17"),
  birthMonth: Yup.string()
    .required("Month is required")
    .matches(/^\d{1,2}$/, "Month must be a number, e.g. 5"),
  birthYear: Yup.string()
    .required("Year is required")
    .matches(/^\d{4}$/, "Year must be 4 digits, e.g. 1980")
    .test("valid-date", "Please enter a real date of birth", function (value) {
      return isValidDateOfBirth({
        day: this.parent.birthDay,
        month: this.parent.birthMonth,
        year: value,
      });
    }),
});

const NAME_INITIAL_VALUES = {
  firstName: "",
  surName: "",
  yearOfBirth: "",
};

const DATE_OF_BIRTH_INITIAL_VALUES = {
  birthDay: "",
  birthMonth: "",
  birthYear: "",
};

// Only a touched part reports, so leaving the day box doesn't flag the month the patient
// hasn't reached yet.
const getDateOfBirthError = ({ errors, touched }) =>
  ["birthDay", "birthMonth", "birthYear"]
    .map((name) => touched[name] && errors[name])
    .find(Boolean);

const FORM_FIELD_BY_PART = { day: "birthDay", month: "birthMonth", year: "birthYear" };

const DateOfBirthFields = ({ errors, touched }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const dateOfBirthError = getDateOfBirthError({ errors, touched });

  const handleChange = ({ day, month, year }) => {
    setFieldValue("birthDay", day, false);
    setFieldValue("birthMonth", month, false);
    setFieldValue("birthYear", year);
  };

  return (
    <fieldset
      className={styles.dateOfBirthFieldset}
      aria-describedby="dateOfBirthHint"
    >
      <legend className={styles.label}>
        Date of Birth <span className={styles.required}>*</span>
      </legend>
      <DateOfBirthInput
        id="dateOfBirth"
        value={{
          day: values.birthDay,
          month: values.birthMonth,
          year: values.birthYear,
        }}
        onChange={handleChange}
        onBlur={(part) => setFieldTouched(FORM_FIELD_BY_PART[part])}
        hasError={!!dateOfBirthError}
        describedBy="dateOfBirthHint"
      />
      <p id="dateOfBirthHint" className={styles.fieldHint}>
        For example, 17/05/1980
      </p>
      {!!dateOfBirthError && (
        <div className={styles.error}>{dateOfBirthError}</div>
      )}
    </fieldset>
  );
};

const NameFields = ({ errors, touched }) => (
  <>
    <div className={styles.fieldGroup}>
      <label htmlFor="firstName" className={styles.label}>
        First Name <span className={styles.required}>*</span>
      </label>
      <Field name="firstName">
        {({ field }) => (
          <OutlinedInput
            {...field}
            id="firstName"
            inputProps={NAME_INPUT_PROPS}
            placeholder="Enter your first name"
            className={styles.input}
            error={touched.firstName && !!errors.firstName}
          />
        )}
      </Field>
      <p className={styles.fieldHint}>
        Use your full first name — double-barrel names are fine, e.g. Sarah Jane
      </p>
      {touched.firstName && errors.firstName && (
        <div className={styles.error}>{errors.firstName}</div>
      )}
    </div>

    <div className={styles.fieldGroup}>
      <label htmlFor="surName" className={styles.label}>
        Surname <span className={styles.required}>*</span>
      </label>
      <Field name="surName">
        {({ field }) => (
          <OutlinedInput
            {...field}
            id="surName"
            inputProps={NAME_INPUT_PROPS}
            placeholder="Enter your surname"
            className={styles.input}
            error={touched.surName && !!errors.surName}
          />
        )}
      </Field>
      <p className={styles.fieldHint}>
        Use your full surname as registered with your practice
      </p>
      {touched.surName && errors.surName && (
        <div className={styles.error}>{errors.surName}</div>
      )}
    </div>

    <div className={styles.fieldGroup}>
      <label htmlFor="yearOfBirth" className={styles.label}>
        Year of Birth <span className={styles.required}>*</span>
      </label>
      <Field name="yearOfBirth">
        {({ field }) => (
          <OutlinedInput
            {...field}
            id="yearOfBirth"
            inputProps={YEAR_INPUT_PROPS}
            placeholder="Enter year of birth"
            className={styles.input}
            error={touched.yearOfBirth && !!errors.yearOfBirth}
          />
        )}
      </Field>
      <p className={styles.fieldHint}>
        Enter your 4-digit year of birth, e.g. 1999
      </p>
      {touched.yearOfBirth && errors.yearOfBirth && (
        <div className={styles.error}>{errors.yearOfBirth}</div>
      )}
    </div>
  </>
);

const InvalidMeetingLink = () => (
  <AuthLayout>
    <div className={styles.container}>
      <div className={styles.errorContainer}>
        <h2 className={styles.title}>Invalid Meeting Link</h2>
        <p className={styles.errorText}>
          This link is invalid or incomplete. Please check the link provided
          by your healthcare provider.
        </p>
      </div>
    </div>
  </AuthLayout>
);

const ParticipantVideoJoinPage = () => {
  const [searchParams] = useSearchParams();
  const [isJoining, setIsJoining] = useState(false);
  const [videoCredentials, setVideoCredentials] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [joinDiagnostics, setJoinDiagnostics] = useState(null);
  const [browserDefaultCameraId, setBrowserDefaultCameraId] = useState(null);
  const { joinVideo } = useParticipantJoinVideoStandalone();

  const meetingId = searchParams.get("meetingId");
  const appointmentId = searchParams.get("appointmentId");

  // The invite link says which form to show (KIN-1953): v=4 is name and year of birth, and
  // anything else — v=3, or no v at all on a link sent before KIN-1953 — is date of birth.
  // It only picks the form; participant-join checks against the practice's current setting.
  const asksForDateOfBirth =
    searchParams.get("v") !== NAME_AND_YEAR_OF_BIRTH_LINK_VERSION;

  const urlDoctorName = searchParams.get("doctorName");
  const urlDate = searchParams.get("date");
  const urlTime = searchParams.get("time");

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(decodeURIComponent(dateStr));
      return date.toLocaleDateString('en-IE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return decodeURIComponent(dateStr);
    }
  };

  // Resolves to the camera the browser chose, or null when access failed.
  // That camera is the OS default, so the call starts it rather than the
  // first in the device list, which on many laptops is an IR camera that
  // sends blank video (KIN-2012).
  const requestMediaPermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true
      });
      const cameraId = stream.getVideoTracks()[0]?.getSettings?.().deviceId ?? "";

      stream.getTracks().forEach(track => track.stop());

      return { cameraId };
    } catch (error) {
      console.error("Media permission error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Please allow camera and microphone access to join the video call");
      } else if (error.name === 'NotFoundError') {
        toast.error("No camera or microphone found. Please connect a device and try again.");
      } else {
        toast.error("Failed to access camera/microphone. Please check your device settings.");
      }

      return null;
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!meetingId || !appointmentId) {
      toast.error(
        "Invalid meeting link. Meeting ID or Appointment ID is missing."
      );
      setSubmitting(false);
      return;
    }

    setIsJoining(true);
    setJoinError(null);

    // "prompt" here means this is the patient's first visit on this browser,
    // which is when audio has been failing until they rejoin.
    const joinRequestedAt = Date.now();
    const microphonePermissionBeforeJoin = await getMicrophonePermissionState();

    try {

      const mediaCheck = await requestMediaPermissions();
      if (!mediaCheck) {
        setIsJoining(false);
        setSubmitting(false);
        return;
      }

      const verificationDetails = asksForDateOfBirth
        ? {
            dateOfBirth: formatDateOfBirth({
              day: values.birthDay,
              month: values.birthMonth,
              year: values.birthYear,
            }),
          }
        : {
            firstName: values.firstName,
            surName: values.surName,
            yearOfBirth: values.yearOfBirth,
          };

      const credentials = await joinVideo({
        appointmentId: parseInt(appointmentId),
        meetingId: meetingId,
        ...verificationDetails,
      });

      const meeting = credentials?.Meeting || credentials?.meeting;
      const attendee = credentials?.Attendee || credentials?.attendee;
      const mediaPlacement =
        meeting?.MediaPlacement ||
        meeting?.mediaPlacement ||
        credentials?.MediaPlacement ||
        credentials?.mediaPlacement;

      const receivedMeetingId =
        credentials?.meetingId || meeting?.MeetingId || meeting?.meetingId;
      const attendeeId =
        credentials?.attendeeId ||
        attendee?.AttendeeId ||
        attendee?.attendeeId;
      const joinToken =
        credentials?.joinToken || attendee?.JoinToken || attendee?.joinToken;

      const audioHostUrl =
        credentials?.audioHostUrl ||
        mediaPlacement?.AudioHostUrl ||
        mediaPlacement?.audioHostUrl;
      const signalingUrl =
        credentials?.signalingUrl ||
        mediaPlacement?.SignalingUrl ||
        mediaPlacement?.signalingUrl;

      if (!receivedMeetingId || !attendeeId || !joinToken) {
        console.error("Missing credentials:", {
          hasMeetingId: !!receivedMeetingId,
          hasAttendeeId: !!attendeeId,
          hasJoinToken: !!joinToken,
          credentialKeys: credentials ? Object.keys(credentials) : null,
          meetingKeys: meeting ? Object.keys(meeting) : null,
          attendeeKeys: attendee ? Object.keys(attendee) : null,
        });
        throw new Error(
          "Invalid video credentials received: Missing required fields"
        );
      }

      if (!audioHostUrl || !signalingUrl) {
        console.error("Missing media configuration:", {
          hasAudioHostUrl: !!audioHostUrl,
          hasSignalingUrl: !!signalingUrl,
        });
        throw new Error(
          "Invalid video credentials received: Missing media configuration"
        );
      }

      // report-error checks reports against this appointment's current meeting.
      setVideoCredentials({ ...credentials, appointmentId: parseInt(appointmentId) });
      setJoinDiagnostics({
        requestedAt: joinRequestedAt,
        microphonePermissionBeforeJoin,
      });
      setBrowserDefaultCameraId(mediaCheck.cameraId || null);
      setShowVideo(true);
      setIsJoining(false);
      toast.success("Connected to video call");
    } catch (error) {
      console.error("Failed to join video call:", error);
      const message = error.response?.data?.message || "Failed to join video call. Please try again.";
      setJoinError(message);
      setIsJoining(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeaveVideo = () => {
    setShowVideo(false);
    setVideoCredentials(null);
    toast.success("Left the meeting");
  };

  if (!meetingId || !appointmentId) {
    return <InvalidMeetingLink />;
  }

  if (showVideo && videoCredentials) {
    const externalMeetingId = videoCredentials?.Meeting?.ExternalMeetingId ||
      videoCredentials?.externalMeetingId;

    const doctorName =
      videoCredentials?.doctorName ||
      videoCredentials?.providerName ||
      (urlDoctorName ? decodeURIComponent(urlDoctorName) : null);
    const practiceName = videoCredentials?.practiceName || null;
    const appointmentTime = videoCredentials?.appointmentTime || videoCredentials?.startTime || null;
    const appointmentDate = videoCredentials?.appointmentDate || null;

    const formatDateTime = () => {
      if (appointmentTime) {
        try {
          const dateObj = new Date(appointmentTime);
          const time = dateObj.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
          });
          const date = dateObj.toLocaleDateString("en-GB", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          });
          return { time, date };
        } catch {
          return { time: null, date: appointmentDate };
        }
      }
      return { time: null, date: appointmentDate };
    };

    const { time, date } = formatDateTime();

    return (
      <div className={styles.videoContainer}>

        <div className={styles.videoHeader}>
          <img
            src={KinesinLogo}
            alt="Kinesin logo"
            className={styles.headerLogo}
          />
          {externalMeetingId && (
            <div className={styles.meetingInfo}>
              <span className={styles.connectedDot}></span>
              <span className={styles.meetingInfoLabel}>Consultation</span>
              <span className={styles.meetingInfoDivider}>|</span>
              <span className={styles.meetingInfoValue}>{externalMeetingId}</span>
            </div>
          )}
        </div>

        <div className={styles.consultationTitleBar}>
          <h2 className={styles.consultationTitle}>Video Consultation</h2>
          {(doctorName || time || date) ? (
            <div className={styles.consultationDetails}>
              {doctorName && (
                <span className={styles.consultationDoctor}>with {doctorName}</span>
              )}
              {(time || date) && (
                <div className={styles.consultationDateTime}>
                  {time && <span className={styles.consultationTime}>{time}</span>}
                  {time && date && <span className={styles.consultationDivider}>•</span>}
                  {date && <span className={styles.consultationDate}>{date}</span>}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.consultationDetails}>
              <span className={styles.consultationDate}>Your consultation is now in progress</span>
            </div>
          )}
        </div>

        <div className={styles.videoWrapper}>
          <InlineVideoCall
            credentials={videoCredentials}
            meetingId={
              videoCredentials?.meetingId ||
              videoCredentials?.Meeting?.MeetingId
            }
            onPopOut={() => {}}
            onLeave={handleLeaveVideo}
            isParticipantView={true}
            joinDiagnostics={joinDiagnostics}
            browserDefaultCameraId={browserDefaultCameraId}
            practiceName={practiceName}
            doctorName={doctorName}
          />
        </div>
        <AppVersion className={styles.versionData} />
      </div>
    );
  }

  return (
    <AuthLayout showVersion={false}>
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <h2 className={styles.title}>Join Video Consultation</h2>

          {(urlDoctorName || urlDate || urlTime) && (
            <div className={styles.appointmentInfo}>
              {urlDoctorName && (
                <div className={styles.appointmentRow}>
                  <span className={styles.appointmentIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </span>
                  <span className={styles.appointmentLabel}>Doctor:</span>
                  <span className={styles.appointmentValue}>{decodeURIComponent(urlDoctorName)}</span>
                </div>
              )}
              {urlDate && (
                <div className={styles.appointmentRow}>
                  <span className={styles.appointmentIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </span>
                  <span className={styles.appointmentLabel}>Date:</span>
                  <span className={styles.appointmentValue}>{formatDisplayDate(urlDate)}</span>
                </div>
              )}
              {urlTime && (
                <div className={styles.appointmentRow}>
                  <span className={styles.appointmentIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </span>
                  <span className={styles.appointmentLabel}>Time:</span>
                  <span className={styles.appointmentValue}>{decodeURIComponent(urlTime)}</span>
                </div>
              )}
            </div>
          )}

          <p className={styles.subtitle}>
            Please enter your details to join the video consultation
          </p>

          <Formik
            initialValues={
              asksForDateOfBirth ? DATE_OF_BIRTH_INITIAL_VALUES : NAME_INITIAL_VALUES
            }
            validationSchema={
              asksForDateOfBirth ? dateOfBirthValidationSchema : nameValidationSchema
            }
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form className={styles.form}>
                {asksForDateOfBirth ? (
                  <DateOfBirthFields errors={errors} touched={touched} />
                ) : (
                  <NameFields errors={errors} touched={touched} />
                )}

                {joinError && (
                  <div className={styles.joinError}>{joinError}</div>
                )}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting || isJoining}
                >
                  {isJoining ? "Launching Video Call..." : "Join Video Call"}
                </button>

                <div className={styles.infoBox}>
                  <p className={styles.infoText}>
                    <strong>Note:</strong> Please ensure your camera and
                    microphone are working before joining the call. You may be
                    asked to grant permissions when the video call opens.
                  </p>
                </div>
              </Form>
            )}
          </Formik>

          <AppVersion className={styles.formVersion} />
        </div>
      </div>
    </AuthLayout>
  );
};

export { ParticipantVideoJoinPage };